import Blackout from "../../entities/blackout";

/**
 * Blackout actions
 *
 * Call order:
 *
 * BLACKOUT_SAVE_QUEUE:	adds the item to the save queue
 * --> BLACKOUT_SAVE:	modifies the state
 * --> BLACKOUT_SAVE_SUCCESS: if all went well
 * --> BLACKOUT_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail BLACKOUT_SAVE_QUEUE is during the call to BLACKOUT_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at BLACKOUT_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: BLACKOUT_SAVE_SUCCESS, BLACKOUT_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or BLACKOUT_PENDING_CHECK (it left the queue).
 */

export const BLACKOUT_FETCH_ONE_QUEUE: any = 'BLACKOUT_FETCH_ONE_QUEUE';
export const BLACKOUT_FETCH_SUCCESS: any = 'BLACKOUT_FETCH_SUCCESS';
export const BLACKOUT_FETCH_FAILURE: any = 'BLACKOUT_FETCH_FAILURE';
export const BLACKOUT_FETCH_ONE_SUCCESS: any = 'BLACKOUT_FETCH_ONE_SUCCESS';
export const BLACKOUT_FETCH_ONE_FAILURE: any = 'BLACKOUT_FETCH_ONE_FAILURE';

export const BLACKOUT_SAVE: any = 'BLACKOUT_SAVE';
export const BLACKOUT_SAVE_QUEUE: any = 'BLACKOUT_SAVE_QUEUE';
export const BLACKOUT_SAVE_SUCCESS: any = 'BLACKOUT_SAVE_SUCCESS';
export const BLACKOUT_SAVE_FAILURE: any = 'BLACKOUT_SAVE_FAILURE';

export const BLACKOUT_SAVE_MANY: any = 'BLACKOUT_SAVE_MANY';
export const BLACKOUT_SAVE_MANY_QUEUE: any = 'BLACKOUT_SAVE_MANY_QUEUE';
export const BLACKOUT_SAVE_MANY_SUCCESS: any = 'BLACKOUT_SAVE_MANY_SUCCESS';
export const BLACKOUT_SAVE_MANY_FAILURE: any = 'BLACKOUT_SAVE_MANY_FAILURE';

export const BLACKOUT_REMOVE: any = 'BLACKOUT_REMOVE';
export const BLACKOUT_REMOVE_QUEUE: any = 'BLACKOUT_REMOVE_QUEUE';
export const BLACKOUT_REMOVE_SUCCESS: any = 'BLACKOUT_REMOVE_SUCCESS';
export const BLACKOUT_REMOVE_FAILURE: any = 'BLACKOUT_REMOVE_FAILURE';

export const BLACKOUT_RESTORE: any = 'BLACKOUT_RESTORE';
export const BLACKOUT_RESTORE_QUEUE: any = 'BLACKOUT_RESTORE_QUEUE';
export const BLACKOUT_RESTORE_SUCCESS: any = 'BLACKOUT_RESTORE_SUCCESS';
export const BLACKOUT_RESTORE_FAILURE: any = 'BLACKOUT_RESTORE_FAILURE';

export const BLACKOUT_RESTORE_MANY: any = 'BLACKOUT_RESTORE_MANY';
export const BLACKOUT_REMOVE_MANY: any = 'BLACKOUT_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const BLACKOUT_SAVE_REVERT: any = 'BLACKOUT_SAVE_REVERT';
export const BLACKOUT_PENDING_CHECK: any = 'BLACKOUT_PENDING_CHECK';

/* Fetch actions */
export const fetchBlackoutSuccess = (blackouts: Blackout[], partial: boolean, lastFetch: number) => ({type: BLACKOUT_FETCH_SUCCESS, blackouts, partial, lastFetch});
export const fetchBlackoutFailure = (err: any) => {
		console.error('BlackoutAction: fetchBlackoutFailure', err);
		return {type: BLACKOUT_FETCH_FAILURE, err};
};
export const fetchOneBlackoutQueue = (blackoutId: string, sentAddedOn?: number) => ({type: BLACKOUT_FETCH_ONE_QUEUE, blackoutId, sentAddedOn});
export const fetchOneBlackoutSuccess = (blackout: Blackout | null, sentAddedOn?: number, blackoutId: string = blackout?.id) => ({type: BLACKOUT_FETCH_ONE_SUCCESS, blackoutId, blackout, sentAddedOn});
export const fetchOneBlackoutFailure = (id: string, err: any) => {
		console.error('BlackoutAction: fetchOneBlackoutFailure', err);
		return {type: BLACKOUT_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveBlackout = (blackout: Blackout) => {
    // console.debug('⮂ BLACKOUT_SAVE action', blackout?.id);
    return {type: BLACKOUT_SAVE, blackout};
}
export const saveBlackoutQueue = (blackout: Blackout, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ BLACKOUT_SAVE_QUEUE action', blackout?.id);
    return {type: BLACKOUT_SAVE_QUEUE, blackout, onQueued};
}
export const saveBlackoutSuccess = (blackout: Blackout, sentAddedOn?: number) => {
    // console.debug('⮂ BLACKOUT_SAVE_SUCCESS action', blackout?.id);
    return {type: BLACKOUT_SAVE_SUCCESS, blackout, sentAddedOn};
}
export const saveBlackoutFailure = (blackout: Blackout, status: number, message: string) => {
    console.warn(`⮂ BLACKOUT_SAVE_FAILURE error saving blackout: HTTP ${status}: ${message}`, blackout?.id);
    return {type: BLACKOUT_SAVE_FAILURE, blackout, status, message};
};

/* SaveMany actions */
export const saveManyBlackouts = (blackouts: Blackout[]) => {
    // console.debug('⮂ BLACKOUT_SAVE_MANY action', blackouts?.map(blackout => blackout?.id).join(','));
    return {type: BLACKOUT_SAVE_MANY, blackouts};
}
export const saveManyBlackoutsQueue = (blackouts: Blackout[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ BLACKOUT_SAVE_MANY_QUEUE action', blackouts?.map(blackout => blackout.id).join(','));
    return {type: BLACKOUT_SAVE_MANY_QUEUE, blackouts, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `blackouts` (OWA-1018) */
export const saveManyBlackoutsSuccess = (blackouts: Blackout[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ BLACKOUT_SAVE_MANY_SUCCESS action', blackouts?.map(blackout => blackout.id).join(','));
    return {type: BLACKOUT_SAVE_MANY_SUCCESS, blackouts, sentAddedOns};
}
export const saveManyBlackoutsFailure = (blackouts: Blackout[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_BLACKOUTS_FAILURE Error saving blackouts: HTTP ${status}: ${message}`, blackouts?.map(blackout => blackout.id).join(','));
		return {type: BLACKOUT_SAVE_MANY_FAILURE, blackouts, status, message};
};

/* Remove actions */
export const removeBlackout = (blackout: Blackout) => ({ type: BLACKOUT_REMOVE, blackout });
export const removeBlackoutQueue = (blackout: Blackout) => ({ type: BLACKOUT_REMOVE_QUEUE, blackout });
export const removeBlackoutSuccess = (blackout: Blackout) => ({ type: BLACKOUT_REMOVE_SUCCESS, blackout });
export const removeBlackoutFailure = (blackout: Blackout, status: number, message: string) => {
	console.error(`Redux: removeBlackoutFailure Error removing blackout: HTTP ${status}: ${message}`);
	return ({ type: BLACKOUT_REMOVE_FAILURE, blackout, status, message });
};

/* Restore actions */
export const restoreBlackout = (blackout: Blackout) => ({ type: BLACKOUT_RESTORE, blackout });
export const restoreBlackoutQueue = (blackout: Blackout) => ({ type: BLACKOUT_RESTORE_QUEUE, blackout });
export const restoreBlackoutSuccess = (blackout: Blackout, sentAddedOn?: number) => ({ type: BLACKOUT_RESTORE_SUCCESS, blackout, sentAddedOn });
export const restoreBlackoutFailure = (blackout: Blackout, status: number, message: string) => {
	console.error(`Redux: restoreBlackoutFailure Error restoring blackout: HTTP ${status}: ${message}`);
	return ({ type: BLACKOUT_RESTORE_FAILURE, blackout, status, message });
};

/* RestoreMany actions */
export const restoreManyBlackouts = (blackouts: Blackout[]) => ({ type: BLACKOUT_RESTORE_MANY, blackouts });

/* RemoveMany actions */
export const removeManyBlackouts = (blackouts: Blackout[]) => ({ type: BLACKOUT_REMOVE_MANY, blackouts });

/* Pending save actions (OWA-1018) */
/**
 * The save `blackout` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertBlackoutSave = (blackout: Blackout, options: {sentAddedOn?: number, current?: Blackout | null, keep?: boolean} = {}) =>
    ({type: BLACKOUT_SAVE_REVERT, blackout, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkBlackoutPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: BLACKOUT_PENDING_CHECK, live, refused, readAt});